import type {
  MainNavigationItemInterface,
  NavigationItemInterface,
  HyperlinkInterface,
  NavSocialItemInterface,
  NavTopItem
} from '@hypercodestudio/basler-components';
import { hasProtocol } from 'ufo';

import { isDefined } from '~/utils/guards/isDefined';
import type { NavigationItemFragment } from '~~/lib/ContentfulGraphqlService';
import { isNavigationItemCollectionFragment } from '~/utils/guards/isNavigationItemCollectionFragment';
import {
  isVisible,
  containsEntraGroup,
  ENTRA_CLAIM_NAME_GROUP
} from '~/utils/entry/isVisible';
import type {
  MinifiedNavItem,
  MinifiedLink,
  MinifiedSocialLink
} from '~/types/MinifiedNavigation';

import { buildUrlString } from '~/utils/buildUrlString';

/**
 * Converts a MinifiedLink to HyperlinkInterface
 */
function minifiedLinkToHyperlink(
  link: MinifiedLink | undefined,
  locale: string
): HyperlinkInterface | undefined {
  if (!link) {
    return undefined;
  }

  // Build full URL for internal links (slugs)
  const isExternalUrl = hasProtocol(link.url, { acceptRelative: true });

  const uri = isExternalUrl
    ? link.url
    : buildUrlString(locale, link.url, link.query, link.anchor);

  return {
    uri,
    target: link.target,
    external: link.external
  };
}

/**
 * Checks if a minified nav item should be visible based on visibility rules
 * Mirrors the logic from isVisible() but works with MinifiedNavItem
 */
function isMinifiedItemVisible(item: MinifiedNavItem): boolean {
  // Public visibility (default when visibility is not set)
  if (!item.visibility || item.visibility === 'Public') {
    return true;
  }

  const { loggedIn, user } = useShopAuth();

  // Logged In visibility - requires authentication
  if (item.visibility === 'Logged In') {
    return loggedIn.value;
  }

  // Restricted visibility - requires specific group membership
  if (item.visibility === 'Restricted') {
    if (!loggedIn.value || !item.allowedGroups?.length) {
      return false;
    }
    const userGroups = user.value?.claims?.[ENTRA_CLAIM_NAME_GROUP];
    if (!Array.isArray(userGroups) || userGroups.length === 0) {
      return false;
    }
    return containsEntraGroup(item.allowedGroups, userGroups as string[]);
  }

  return true;
}

/**
 * Creates a MainNavigationItemInterface from a MinifiedNavItem
 * Used for the header main navigation
 */
export function useCreateMainNavItemFromMinified(
  item: MinifiedNavItem | null | undefined,
  isItemActive: boolean,
  depth = 0
): MainNavigationItemInterface | undefined {
  const locale = useLocale();

  if (!item || depth > 3 || !isMinifiedItemVisible(item)) {
    return;
  }

  const items = item.items
    ?.map((n) => useCreateMainNavItemFromMinified(n, false, depth + 1))
    .filter(isDefined);

  const moreLink = item.moreLink
    ? {
        link: minifiedLinkToHyperlink(item.moreLink.link, locale.value) ?? {},
        text: item.moreLink.text
      }
    : undefined;

  const specialLink = item.specialLink
    ? {
        link:
          minifiedLinkToHyperlink(item.specialLink.link, locale.value) ?? {},
        text: item.specialLink.text
      }
    : undefined;

  return {
    items: items?.length ? items : undefined,
    altText: item.altText,
    isTwoColumns: item.isTwoColumns,
    moreLink,
    specialLink,
    hideOnDesktop: item.hideOnDesktop,
    hideOnMobile: item.hideOnMobile,
    text: item.text,
    link: item.link
      ? minifiedLinkToHyperlink(item.link, locale.value)
      : undefined,
    isActive: isItemActive
  } satisfies MainNavigationItemInterface;
}

/**
 * @deprecated Use useCreateMainNavItemFromMinified for minified navigation trees.
 * Kept for legacy call sites that still pass full NavigationItemFragment.
 */
export function useCreateMainNavItem(
  item: NavigationItemFragment | null | undefined,
  isItemActive: boolean,
  depth = 0
): MainNavigationItemInterface | undefined {
  if (!item || depth > 3 || !isVisible(item)) {
    return;
  }

  const subItems = isNavigationItemCollectionFragment(item)
    ? item.navigationItemsCollection?.items
    : undefined;

  const moreLink =
    item.moreLink && item.moreLinkText
      ? {
          link: useBuildLinkInterface(item.moreLink) ?? {},
          text: item.moreLinkText
        }
      : undefined;

  const items = subItems
    ?.map((n) => useCreateMainNavItem(n, false, depth + 1))
    .filter(isDefined);

  return {
    items: items?.length ? items : undefined,
    altText: item.altText ?? undefined,
    isTwoColumns: item.isTwoColumns ?? undefined,
    moreLink,
    specialLink:
      item.specialLink && item.specialLinkText
        ? {
            link: useBuildLinkInterface(item.specialLink) ?? {},
            text: item.specialLinkText
          }
        : undefined,
    hideOnDesktop: item.hideOnDesktop ?? undefined,
    hideOnMobile: item.hideOnMobile ?? undefined,
    text: item.linkText ?? '',
    link:
      item.internalPage || item.externalLink
        ? useBuildLinkInterface(item)
        : undefined,
    isActive: isItemActive
  } satisfies MainNavigationItemInterface;
}

export function mapMinifiedSocialLinkToNavSocialItem(
  item: MinifiedSocialLink,
  locale: string
): NavSocialItemInterface | undefined {
  const hyperlink = minifiedLinkToHyperlink(item.link, locale);
  if (!hyperlink?.uri) {
    return undefined;
  }

  return {
    iconName: item.icon ?? '',
    text: item.text,
    link: {
      uri: hyperlink.uri,
      external: hyperlink.external ?? item.link.target === '_blank',
      target: hyperlink.target
    }
  } satisfies NavSocialItemInterface;
}

export function mapMinifiedNavItemToNavTopItem(
  item: MinifiedNavItem,
  locale: string
): NavTopItem | undefined {
  if (!isMinifiedItemVisible(item)) {
    return undefined;
  }

  const link = minifiedLinkToHyperlink(item.link, locale);
  if (!link) {
    return undefined;
  }

  return {
    label: item.text,
    link,
    iconName: item.icon ?? ''
  };
}

export function mapMinifiedNavItemToAccountWidgetLink(
  item: MinifiedNavItem,
  locale: string
):
  | {
      title: string;
      icon?: { name: string };
      link: HyperlinkInterface;
    }
  | undefined {
  if (!isMinifiedItemVisible(item)) {
    return;
  }

  const link = item.link
    ? minifiedLinkToHyperlink(item.link, locale)
    : undefined;
  if (!link) {
    return undefined;
  }
  return {
    title: item.text,
    icon: item.icon ? { name: item.icon } : undefined,
    link
  };
}

export function useCreateNavItemFromMinified(
  item: MinifiedNavItem | null | undefined,
  withAbsolutePath = false,
  depth = 0
): NavigationItemInterface | undefined {
  const locale = useLocale();

  if (!item || depth > 3 || !isMinifiedItemVisible(item)) {
    return;
  }

  return {
    items: item.items
      ?.map((child) =>
        useCreateNavItemFromMinified(child, withAbsolutePath, depth + 1)
      )
      .filter(isDefined),
    text: item.text,
    link: item.link
      ? minifiedLinkToHyperlink(item.link, locale.value)
      : undefined,
    isActive: false
  };
}

export function useCreateNavItem(
  item?: NavigationItemFragment | null,
  withAbsolutePath = false,
  ignoreShowInNavigation = false
): NavigationItemInterface | undefined {
  if (
    !item ||
    (isDefined(item.showInNavigation) &&
      !item.showInNavigation &&
      !ignoreShowInNavigation)
  ) {
    return;
  }

  if (!isVisible(item, ignoreShowInNavigation)) {
    return;
  }

  const subItems = isNavigationItemCollectionFragment(item)
    ? item.navigationItemsCollection?.items
    : undefined;

  return {
    items: subItems
      ?.map((n) => useCreateNavItem(n, withAbsolutePath))
      .filter(isDefined),
    text: item.linkText ?? '',
    link: useBuildLinkInterface(item, withAbsolutePath),
    isActive: false
  };
}
